import { useTranslation } from 'react-i18next'
import type { DebitCreditSetResult, FormState, TdLine } from '../types/testdata.types'
import { previewCreditMessage } from '../hooks/useDebitCreditSet'

const inputCls = 'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'

interface Props {
  enabled: boolean
  onToggle: (value: boolean) => void
  creditDocumentNumber: string
  onCreditDocumentNumberChange: (value: string) => void
  creditedLineIndex: number | null
  onCreditedLineIndexChange: (value: number | null) => void
  form: FormState
  lines: TdLine[]
  locale: string
  result: DebitCreditSetResult | null
  onClearResult: () => void
}

/** Toggle + velden voor de debet/credit-set: genereert in één actie een debetfactuur +
 * een creditnota die de debetfactuur crediteert (met automatische correctieboodschap).
 * Standaard wordt de hele factuur gecrediteerd (alle regels); optioneel kan één specifieke
 * debetregel geselecteerd worden zodat alleen die regel wordt gecrediteerd. */
export function DebitCreditSetPanel({
  enabled, onToggle, creditDocumentNumber, onCreditDocumentNumberChange,
  creditedLineIndex, onCreditedLineIndexChange,
  form, lines, locale, result, onClearResult,
}: Props) {
  const { t } = useTranslation()
  const preview = previewCreditMessage(creditDocumentNumber, form.document_number, lines, locale, creditedLineIndex)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => onToggle(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('debit_credit_set.toggle_label')}</span>
      </label>
      {enabled && (
        <>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('debit_credit_set.toggle_hint')}</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('debit_credit_set.field_debit_number')}</label>
              <input className={inputCls} value={form.document_number} disabled />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('debit_credit_set.field_credit_number')}</label>
              <input
                className={inputCls}
                value={creditDocumentNumber}
                onChange={e => onCreditDocumentNumberChange(e.target.value)}
                placeholder="CN-260825-XX-01"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('debit_credit_set.field_credited_line')}</label>
              <select
                className={inputCls}
                value={creditedLineIndex ?? ''}
                onChange={e => onCreditedLineIndexChange(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">{t('debit_credit_set.credited_line_all')}</option>
                {lines.map((line, idx) => (
                  <option key={idx} value={idx}>
                    {line.article_number} — {line.description || ''} ({line.quantity} × {line.unit_price})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {preview && (
            <div className="mt-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-lg px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
              <span className="font-medium">{t('debit_credit_set.preview_label')}: </span>{preview}
            </div>
          )}
          {result && (
            <div className="mt-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/40 rounded-lg px-3 py-2 text-xs text-green-800 dark:text-green-300 flex items-center justify-between">
              <span>
                {t('debit_credit_set.result_summary', {
                  debit: result.documents.invoice, credit: result.documents.credit_note,
                })}
              </span>
              <button onClick={onClearResult} className="ml-3 text-green-600 hover:text-green-800">×</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
